import clsx from 'clsx';
import React from 'react';
import useTextHtml from './useTextHtml';

/**
 * TextHtml - Renders HTML content with optional math (KaTeX) support.
 *
 * The v2 replacement of `core/modules/atoms/TextHtml`. Renders the HTML as-is (same
 * behavior as v1) and removes the buggy default `id='useMathRender'` that made every
 * instance share the same DOM id. External SCSS overrides continue to target the
 * `.text-html` class (kept intentionally, without the `.ui-*` prefix, so the external
 * SCSS files that customize it keep working without changes).
 *
 * NOTE: DOMPurify sanitization was intentionally left out of this migration to avoid a
 * behavior change in production; it will be reintroduced in a follow-up ticket once it
 * can be tested against real content.
 *
 * isAuthored: marks the content as authored (created by an editor/teacher); it then
 * takes the program's custom typography. The CSS rule lives in core
 * (`_program-theming.scss`), not in the DS, because it consumes v1 vars
 * (--font-second-custom).
 */
export type TextHtmlProps = {
  className?: string;
  id?: string;
  isAuthored?: boolean;
  isBlockFormatted?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  text?: string;
  title?: string;
};

function TextHtml({
  className,
  id,
  isAuthored = false,
  isBlockFormatted = false,
  onClick,
  text = '',
  title,
}: TextHtmlProps): JSX.Element {
  const { htmlContent, rootRef } = useTextHtml({ isBlockFormatted, text });

  const interactiveProps = onClick
    ? {
        onClick,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
          }
        },
        role: 'button',
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={clsx('text-html', { 'text-html--authored': isAuthored }, className)}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
      id={id}
      ref={rootRef}
      title={title}
      {...interactiveProps}
    />
  );
}

export default TextHtml;
